<script setup lang="ts">
import {
    faCircle as fasCircle,
    faTrain,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import type TrainPackage from '@itinerary/trip/train/TrainPackage';
import { type MidStationResponse } from '@clients/TrainApi';

type Station = {
    isOrigin: boolean;
    isDestination: boolean;
    key: string;
    title: string;
    time: string;
};

const { tripPackage } = defineProps<{
    tripPackage: TrainPackage;
}>();
const trainModule = useTrainModule();
const loading = ref(true);
const midStations = ref<MidStationResponse[]>([]);
const stations = computed<Station[]>(() =>
    midStations.value.map(s => ({
        key: s.stationId?.toString() ?? '',
        title: s.station ?? '',
        time: s.time?.slice(0, 5) ?? '',
        isOrigin: s.stationId?.toString() === tripPackage.originCode.toString(),
        isDestination:
            s.stationId?.toString() === tripPackage.destinationCode.toString(),
    }))
);
const getPackageMidStations = async () => {
    try {
        midStations.value = await trainModule.getPackageMidStations(
            tripPackage.trainCapacityId
        );
    } catch (e) {
        reportException(e);
    } finally {
        loading.value = false;
    }
};
onBeforeMount(() => getPackageMidStations());
</script>

<template>
    <div class="train-mid-stations">
        <mr-loading v-if="loading" />
        <div
            v-for="station in stations"
            :key="station.key"
            class="station-wrapper"
        >
            <div
                class="station-container"
                :class="{
                    origin: station.isOrigin,
                    destination: station.isDestination,
                }"
            >
                <mr-icon
                    class="station-icon"
                    :icon="
                        station.isOrigin
                            ? faTrain
                            : station.isDestination
                              ? fasCircle
                              : faCircle
                    "
                    :type="
                        station.isOrigin || station.isDestination
                            ? 'solid'
                            : 'regular'
                    "
                    color="var(--blue-400)"
                />
                <div class="station-title">
                    {{ station.title }}
                </div>
                <div class="station-arrival-time">({{ station.time }})</div>
            </div>
            <div class="divider" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.train-mid-stations {
    width: 100%;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    overflow: hidden;
    padding: 40px 0;
}

.station-wrapper {
    flex-basis: 8%;
    flex-shrink: 0;
    flex-grow: 1;
    position: relative;
    margin-bottom: 4px;
    &:last-child {
        // margin-left: auto;
        .divider {
            display: none;
        }
    }
}

.station-container {
    display: flex;
    height: 70px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0 4px;
    &.origin,
    &.destination {
        &.origin {
            .station-icon {
                margin-right: -2px;
            }
        }
        .station-title,
        .station-arrival-time {
            @include tg-m($color: usePalette());
        }
    }
    .station-icon {
        background: usePalette($p-gray, $p-100);
        flex-shrink: 0;
        z-index: 1;
    }

    .station-title {
        margin-top: 4px;
    }

    .station-title,
    .station-arrival-time {
        // text
        white-space: nowrap;
        @include tg-s;
        text-align: center;
    }
}

.divider {
    position: absolute;
    top: 8px;
    right: 50%;
    width: 100%;
    height: 3px;
    border-bottom: 3px dashed usePalette($p-cool-gray);
    z-index: 0;
}

@media (max-width: $app-mobile-max-width) {
    .train-mid-stations {
        flex-direction: column;
        align-items: flex-start;
        flex-wrap: nowrap;
        overflow: auto;
    }

    .station-container {
        flex-direction: row;
        align-items: center;
        margin-right: 6px;
        height: 40px;

        .station-title {
            margin: 0 4px;
        }
    }

    .divider {
        top: 50%;
        right: 16px;
        width: 3px;
        height: 100%;
        border-bottom: unset;
        border-right: 3px dashed usePalette($p-cool-gray);
    }
}
</style>
